import React, { useState, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Sparkles, Target, Users, Award, TrendingUp, MessageCircle } from "lucide-react";
import Footer from "../components/Footer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Home = () => {
  const { login } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
    grade: "",
    user_type: "school_student",
    interested_subjects: "",
    career_ambitions: "",
    preferred_career_field: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    preferred_currency: "USD"
  });
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Fetch countries and currencies on component mount
  React.useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const [countriesRes, currenciesRes] = await Promise.all([
          axios.get(`${API}/countries`),
          axios.get(`${API}/currencies`)
        ]);
        setCountries(countriesRes.data.countries || []);
        setCurrencies(currenciesRes.data.currencies || []);
      } catch (error) {
        console.error("Failed to load countries/currencies", error);
      }
    };
    fetchLocationData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { 
            ...formData, 
            grade: formData.grade ? parseInt(formData.grade) : null,
            user_type: formData.user_type,
            interested_subjects: formData.interested_subjects ? formData.interested_subjects.split(',').map(s => s.trim()).filter(s => s) : [],
            city: formData.city || null,
            state: formData.state || null,
            zip_code: formData.zip_code || null,
            country: formData.country || null,
            preferred_currency: formData.preferred_currency || "USD"
          };

      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Left Section - Hero */}
      <div className="w-full lg:w-1/2 bg-gradient-to-br from-primary via-primary/90 to-primary/80 p-8 lg:p-12 flex flex-col justify-between text-white relative overflow-hidden min-h-[45vh] lg:min-h-screen">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-lg">
              <Sparkles className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">SkillEdge</h1>
              <p className="text-sm opacity-90">Empowering Career Readiness through Smart Guidance</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
              Discover Your
              <br />
              <span className="text-accent">Dream Career</span>
            </h2>
            <p className="text-lg lg:text-xl leading-relaxed opacity-95 max-w-xl">
              AI-powered career guidance platform for students. Empowering your career readiness with personalized assessments, expert consultations, and smart career recommendations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 relative z-10 mt-8 lg:mt-0">
          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow" data-testid="feature-ai-powered">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
              <Target className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-semibold text-lg mb-1 text-foreground">AI-Powered</h3>
            <p className="text-sm text-muted-foreground">Smart career recommendations</p>
          </div>
          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow" data-testid="feature-expert-guidance">
            <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center mb-3">
              <Users className="w-6 h-6 text-success" />
            </div>
            <h3 className="font-semibold text-lg mb-1 text-foreground">Expert Guidance</h3>
            <p className="text-sm text-muted-foreground">Book live consultations</p>
          </div>
          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow" data-testid="feature-assessments">
            <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center mb-3">
              <Award className="w-6 h-6 text-warning" />
            </div>
            <h3 className="font-semibold text-lg mb-1 text-foreground">Grade-Specific</h3>
            <p className="text-sm text-muted-foreground">Tailored assessments</p>
          </div>
          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow" data-testid="feature-progress">
            <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center mb-3">
              <TrendingUp className="w-6 h-6 text-success" />
            </div>
            <h3 className="font-semibold text-lg mb-1 text-foreground">Track Progress</h3>
            <p className="text-sm text-muted-foreground">Gamified learning</p>
          </div>
        </div>
      </div>

      {/* Right Section - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-muted/30">
        <Card className="w-full max-w-md shadow-lg border-border" data-testid="auth-card">
          <CardHeader className="text-center space-y-3 pb-6">
            <div className="mx-auto mb-2 w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-md">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold">Welcome to SkillEdge</CardTitle>
            <CardDescription className="text-base text-muted-foreground">
              Empowering Career Readiness through Smart Guidance
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Tabs defaultValue="login" className="w-full" onValueChange={(val) => setIsLogin(val === "login")}>
              <TabsList className="grid w-full grid-cols-2 mb-6 h-11" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="login-tab" className="text-base font-medium">
                  Login
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab" className="text-base font-medium">
                  Register
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      name="email"
                      placeholder="student@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                    {loading ? "Logging in..." : "Login"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      type="text"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      name="email"
                      placeholder="student@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-mobile">Mobile Number (Optional)</Label>
                    <Input
                      id="register-mobile"
                      type="tel"
                      name="mobile"
                      placeholder="+91 9876543210"
                      value={formData.mobile || ''}
                      onChange={handleChange}
                      data-testid="register-mobile-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-user-type">I am a</Label>
                    <select
                      id="register-user-type"
                      name="user_type"
                      value={formData.user_type}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-md"
                      required
                      data-testid="register-user-type-select"
                    >
                      <option value="school_student">School Student (Grade 1-12)</option>
                      <option value="college_student">College Student</option>
                      <option value="job_seeker">Job Seeker / Professional</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {formData.user_type === "school_student" && (
                    <div className="space-y-2">
                      <Label htmlFor="register-grade">Current Grade (1-12)</Label>
                      <Input
                        id="register-grade"
                        type="number"
                        name="grade"
                        placeholder="10"
                        min="1"
                        max="12"
                        value={formData.grade}
                        onChange={handleChange}
                        required
                        data-testid="register-grade-input"
                      />
                    </div>
                  )}
                  
                  {/* Profile Fields */}
                  <div className="space-y-2">
                    <Label htmlFor="register-subjects">Interested Subjects (comma-separated)</Label>
                    <Input
                      id="register-subjects"
                      type="text"
                      name="interested_subjects"
                      placeholder="e.g., Mathematics, Physics, Computer Science"
                      value={formData.interested_subjects}
                      onChange={handleChange}
                      data-testid="register-subjects-input"
                    />
                    <p className="text-xs text-gray-500">Optional: Helps us provide better recommendations</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-ambitions">Career Ambitions</Label>
                    <Input
                      id="register-ambitions"
                      type="text"
                      name="career_ambitions"
                      placeholder="e.g., Become a software engineer, doctor, entrepreneur"
                      value={formData.career_ambitions}
                      onChange={handleChange}
                      data-testid="register-ambitions-input"
                    />
                    <p className="text-xs text-gray-500">Optional: What are your career goals?</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-field">Preferred Career Field</Label>
                    <Input
                      id="register-field"
                      type="text"
                      name="preferred_career_field"
                      placeholder="e.g., Technology, Healthcare, Business, Arts"
                      value={formData.preferred_career_field}
                      onChange={handleChange}
                      data-testid="register-field-input"
                    />
                    <p className="text-xs text-gray-500">Optional: Which field interests you most?</p>
                  </div>
                  
                  {/* Location Fields */}
                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-semibold mb-3 text-sm">Location & Currency (Optional)</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="register-city">City</Label>
                        <Input
                          id="register-city"
                          type="text"
                          name="city"
                          placeholder="Mumbai"
                          value={formData.city}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-state">State/Province</Label>
                        <Input
                          id="register-state"
                          type="text"
                          name="state"
                          placeholder="Maharashtra"
                          value={formData.state}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="space-y-2">
                        <Label htmlFor="register-zip">ZIP/Postal Code</Label>
                        <Input
                          id="register-zip"
                          type="text"
                          name="zip_code"
                          placeholder="400001"
                          value={formData.zip_code}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-country">Country</Label>
                        <Select
                          value={formData.country}
                          onValueChange={(value) => setFormData({...formData, country: value})}
                        >
                          <SelectTrigger id="register-country">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {countries.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mt-3">
                      <Label htmlFor="register-currency">Preferred Currency</Label>
                      <Select
                        value={formData.preferred_currency}
                        onValueChange={(value) => setFormData({...formData, preferred_currency: value})}
                      >
                        <SelectTrigger id="register-currency">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {currencies.map((currency) => (
                            <SelectItem key={currency.code} value={currency.code}>
                              {currency.symbol} {currency.code} - {currency.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">Used for subscription pricing</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Home;